/**
 * meta/overlay.js — the desktop DOM view of every meta system.
 *
 * VR players browse the in-world panels; on desktop those panels are small,
 * far away, and awkward to read, so the same data also renders as a normal HTML
 * overlay. Both read the same modules — there is no second copy of the rules
 * here, only a second presentation.
 *
 * Styles and markup are injected rather than added to `index.html`, so this
 * module can be dropped in or out without touching a shared file.
 *
 * @module meta/overlay
 */

import { STORE_CONFIG, EVENTS } from '../constants.js';
import { economy } from '../core/save.js';
import { bus } from '../core/events.js';
import { sets } from './prize-sets.js';
import { cosmetics } from './cosmetics-catalog.js';
import { dailyLogin } from './daily-login.js';
import { challenges } from './challenges.js';
import { leaderboards } from './leaderboards.js';
import { store } from './store.js';

const STYLE_ID = 'cc-meta-style';
const ROOT_ID = 'cc-meta-overlay';

const CSS = `
#${ROOT_ID}{position:fixed;inset:0;z-index:60;display:none;align-items:center;
 justify-content:center;background:rgba(8,11,22,.72);font-family:"Trebuchet MS",sans-serif;
 backdrop-filter:blur(3px)}
#${ROOT_ID}.open{display:flex}
#${ROOT_ID} .card{width:min(860px,92vw);max-height:86vh;overflow:hidden;display:flex;
 flex-direction:column;background:#131a2f;border:2px solid rgba(255,209,102,.55);
 border-radius:16px;box-shadow:0 24px 70px rgba(0,0,0,.6);color:#e8eefc}
#${ROOT_ID} .head{display:flex;align-items:center;gap:14px;padding:14px 20px;
 border-bottom:1px solid rgba(255,255,255,.1)}
#${ROOT_ID} .head h2{margin:0;font-size:22px;color:#ffd166;letter-spacing:.5px;flex:1}
#${ROOT_ID} .wallet{display:flex;gap:10px;font-size:15px}
#${ROOT_ID} .wallet b{color:#ffd166}
#${ROOT_ID} .x{cursor:pointer;border:0;background:rgba(255,255,255,.08);color:#e8eefc;
 border-radius:8px;padding:6px 11px;font-size:15px}
#${ROOT_ID} .x:hover{background:rgba(255,209,102,.25)}
#${ROOT_ID} .tabs{display:flex;gap:8px;padding:12px 20px 0;flex-wrap:wrap}
#${ROOT_ID} .tabs button{cursor:pointer;border:0;border-radius:8px;padding:7px 14px;
 font-size:14px;background:rgba(255,255,255,.06);color:#9aa6c4}
#${ROOT_ID} .tabs button.on{background:rgba(255,209,102,.22);color:#ffd166;font-weight:bold}
#${ROOT_ID} .body{padding:16px 20px 20px;overflow-y:auto}
#${ROOT_ID} .demo{background:rgba(255,143,143,.14);border:1px solid rgba(255,143,143,.5);
 color:#ffb0b0;border-radius:8px;padding:8px 12px;margin-bottom:14px;font-size:14px;text-align:center}
#${ROOT_ID} .grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(250px,1fr));gap:12px}
#${ROOT_ID} .set{background:rgba(255,255,255,.05);border-radius:12px;padding:13px}
#${ROOT_ID} .set h3{margin:0 0 3px;font-size:16px;color:#ffd166}
#${ROOT_ID} .set .meta{font-size:12px;color:#7f8bad;margin-bottom:9px}
#${ROOT_ID} .locked{opacity:.55}
#${ROOT_ID} .bar{height:6px;border-radius:3px;background:rgba(255,255,255,.1);margin:8px 0}
#${ROOT_ID} .bar i{display:block;height:100%;border-radius:3px;background:#6fe7c4}
#${ROOT_ID} .piece{display:flex;align-items:center;gap:8px;padding:5px 0;font-size:13px;
 border-top:1px solid rgba(255,255,255,.05)}
#${ROOT_ID} .piece span{flex:1}
#${ROOT_ID} .piece .cost{color:#ffd166;font-weight:bold}
#${ROOT_ID} .piece.owned span{color:#6fe7c4}
#${ROOT_ID} button.buy{cursor:pointer;border:0;border-radius:6px;padding:4px 10px;font-size:12px;
 background:rgba(255,209,102,.22);color:#ffd166;font-weight:bold}
#${ROOT_ID} button.buy:hover:not(:disabled){background:rgba(255,209,102,.4)}
#${ROOT_ID} button.buy:disabled{background:rgba(255,255,255,.04);color:#5a6480;cursor:default}
#${ROOT_ID} .cal{display:grid;grid-template-columns:repeat(10,1fr);gap:6px}
#${ROOT_ID} .day{aspect-ratio:1;border-radius:8px;background:rgba(255,255,255,.05);
 display:flex;flex-direction:column;align-items:center;justify-content:center;font-size:11px;
 color:#9aa6c4;border:2px solid transparent}
#${ROOT_ID} .day b{font-size:14px;color:#e8eefc}
#${ROOT_ID} .day.claimed{background:rgba(111,231,196,.18);color:#6fe7c4}
#${ROOT_ID} .day.claimed b{color:#6fe7c4}
#${ROOT_ID} .day.today{border-color:#ffd166;background:rgba(255,209,102,.2)}
#${ROOT_ID} .day.today b{color:#ffd166}
#${ROOT_ID} .day.milestone{outline:1px dashed rgba(203,166,255,.6)}
#${ROOT_ID} .claim{display:block;width:100%;margin-top:16px;cursor:pointer;border:0;
 border-radius:10px;padding:12px;font-size:17px;font-weight:bold;background:#ffd166;color:#131a2f}
#${ROOT_ID} .claim:disabled{background:rgba(255,255,255,.08);color:#5a6480;cursor:default}
#${ROOT_ID} table{width:100%;border-collapse:collapse;font-size:14px}
#${ROOT_ID} td{padding:6px 8px;border-bottom:1px solid rgba(255,255,255,.06)}
#${ROOT_ID} tr.me td{background:rgba(255,209,102,.16);color:#ffd166;font-weight:bold}
#${ROOT_ID} td.r{text-align:right}
#${ROOT_ID} .row{display:flex;align-items:center;gap:10px;background:rgba(255,255,255,.05);
 border-radius:10px;padding:11px 14px;margin-bottom:9px}
#${ROOT_ID} .row .t{flex:1}
#${ROOT_ID} .row .t b{display:block;font-size:15px}
#${ROOT_ID} .row .t i{font-style:normal;font-size:12px;color:#7f8bad}
#${ROOT_ID} .hint{margin-top:14px;font-size:12px;color:#7f8bad;text-align:center}
`;

/** Screens the overlay can show. */
const SCREENS = ['sets', 'wardrobe', 'daily', 'boards', 'challenges', 'store'];

const TAB_LABELS = {
  sets: 'Prize Sets',
  wardrobe: 'Wardrobe',
  daily: 'Daily Reward',
  boards: 'Leaderboards',
  challenges: 'Challenges',
  store: 'Store',
};

const esc = (s) => String(s).replace(/[&<>"]/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]
));

class MetaOverlay {
  constructor() {
    this.root = null;
    this.screen = 'sets';
    this.boardScope = 'weekly';
    this.boardStall = null;
    this._open = false;
  }

  /** Inject styles and markup. Idempotent. */
  init() {
    if (this.root || typeof document === 'undefined') return this;

    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = CSS;
      document.head.appendChild(style);
    }

    this.root = document.createElement('div');
    this.root.id = ROOT_ID;
    this.root.innerHTML = `
      <div class="card">
        <div class="head">
          <h2 id="cc-meta-title">Prize Desk</h2>
          <div class="wallet" id="cc-meta-wallet"></div>
          <button class="x" data-action="close">Esc</button>
        </div>
        <div class="tabs" id="cc-meta-tabs"></div>
        <div class="body" id="cc-meta-body"></div>
      </div>`;
    document.body.appendChild(this.root);

    this.root.addEventListener('click', (e) => this._onClick(e));
    // Clicking the dim backdrop closes, but a click inside the card must not.
    this.root.addEventListener('mousedown', (e) => {
      if (e.target === this.root) this.close();
    });

    bus.on(EVENTS.WALLET_CHANGE, () => { if (this._open) this.render(); });
    return this;
  }

  /** @returns {boolean} */
  isOpen() {
    return this._open;
  }

  /** @returns {string} */
  currentScreen() {
    return this.screen;
  }

  /** @param {string} [screen] */
  open(screen) {
    this.init();
    if (screen && SCREENS.includes(screen)) this.screen = screen;
    this._open = true;
    this.root.classList.add('open');
    this.render();
    bus.emit(EVENTS.AUDIO_PLAY, { name: 'ui-open' });
  }

  close() {
    if (!this.root) return;
    this._open = false;
    this.root.classList.remove('open');
    bus.emit(EVENTS.AUDIO_PLAY, { name: 'ui-close' });
  }

  /** @param {string} screen */
  toggle(screen) {
    if (this._open && (!screen || screen === this.screen)) this.close();
    else this.open(screen);
  }

  // ── Rendering ───────────────────────────────────────────────────────────

  render() {
    if (!this.root || !this._open) return;
    const w = economy.wallet();

    this.root.querySelector('#cc-meta-title').textContent = TAB_LABELS[this.screen] || 'Prize Desk';
    this.root.querySelector('#cc-meta-wallet').innerHTML =
      `<span>🎟 <b>${w.tickets}</b></span><span>🪙 <b>${w.tokens}</b></span><span>⭐ <b>${w.stars}</b></span>`;

    const tabs = SCREENS.filter((s) => s !== 'store' || store.isEnabled());
    this.root.querySelector('#cc-meta-tabs').innerHTML = tabs.map((s) =>
      `<button data-action="tab" data-tab="${s}" class="${s === this.screen ? 'on' : ''}">${TAB_LABELS[s]}</button>`,
    ).join('');

    const body = this.root.querySelector('#cc-meta-body');
    body.innerHTML = this[`_${this.screen}`] ? this[`_${this.screen}`]() : '';
  }

  /** @private GDD 8.5 — set grid with per-piece purchase. */
  _sets() {
    const tokens = economy.tokens();
    return `<div class="grid">${sets.all().map((s) => {
      const pieces = s.pieceList.map((p) => {
        const affordable = s.available && !p.owned && tokens >= p.cost;
        const btn = p.owned
          ? '<span class="cost">✓</span>'
          : `<button class="buy" data-action="buy-piece" data-piece="${p.id}" ${affordable ? '' : 'disabled'}>${p.cost} 🪙</button>`;
        return `<div class="piece ${p.owned ? 'owned' : ''}"><span>${esc(p.name)}</span>${btn}</div>`;
      }).join('');

      const status = s.complete
        ? `<span style="color:#6fe7c4">COMPLETE — ${esc(s.cosmeticName)} unlocked</span>`
        : s.available
          ? `Unlocks ${esc(s.cosmeticName)}`
          : `<span style="color:#ffb0b0">${esc(s.lockLabel)} · ${s.returnsInDays}d</span>`;

      return `<div class="set ${s.available ? '' : 'locked'}">
        <h3>${esc(s.name)}</h3>
        <div class="meta">${s.owned}/${s.total} pieces · ${s.totalTokens} 🪙 total<br>${status}</div>
        <div class="bar"><i style="width:${Math.round((s.owned / s.total) * 100)}%"></i></div>
        ${pieces}</div>`;
    }).join('')}</div>`;
  }

  /** @private GDD 3.4 — equip what you have earned. */
  _wardrobe() {
    const list = cosmetics.catalog();
    const owned = list.filter((c) => c.owned);
    if (!owned.length) {
      return `<div class="hint">No cosmetics yet. Complete a prize set — or a 30-day
        login streak — to unlock one.</div>
        <div class="grid" style="margin-top:14px">${list.map((c) =>
    `<div class="set locked"><h3>${esc(c.name)}</h3>
            <div class="meta">${esc(c.slot)} slot · locked<br>${esc(c.blurb || '')}</div></div>`).join('')}</div>`;
    }

    return owned.map((c) => `<div class="row">
      <div class="t"><b>${esc(c.name)}</b><i>${esc(c.slot)} slot · ${esc(c.blurb || '')}</i></div>
      <button class="buy" data-action="${c.equipped ? 'unequip' : 'equip'}"
        data-slot="${c.slot}" data-id="${c.id}">${c.equipped ? 'Take off' : 'Wear'}</button>
    </div>`).join('') + `<div class="hint">Equipped items show on your goose in the hub.</div>`;
  }

  /** @private GDD 8.7 — the 30-day grid. */
  _daily() {
    const state = dailyLogin.state();
    const cells = dailyLogin.calendar().map((d) => {
      const cls = ['day', d.state, d.milestone ? 'milestone' : ''].filter(Boolean).join(' ');
      const star = d.stars ? `<span>⭐${d.stars}</span>` : '';
      return `<div class="${cls}"><b>${d.day}</b><span>🎟${d.tickets}</span>${star}</div>`;
    }).join('');

    const reward = state.nextReward;
    const label = state.due && reward
      ? `Claim day ${state.nextDay} — +${reward.tickets} tickets${reward.stars ? ` +${reward.stars}⭐` : ''}${reward.cosmetic ? ' + exclusive cosmetic' : ''}`
      : 'Already claimed today — come back tomorrow';

    return `<div class="cal">${cells}</div>
      <button class="claim" data-action="claim-daily" ${state.due ? '' : 'disabled'}>${label}</button>
      <div class="hint">Streak: day ${state.streak} of 30 · miss a day and it resets to day 1.
        Day 30 grants an exclusive cosmetic (next: ${esc(cosmetics.def(state.nextExclusive)?.name || state.nextExclusive)}).</div>`;
  }

  /** @private GDD 7.6 — per-stall boards. */
  _boards() {
    const stalls = leaderboards.stalls();
    if (!this.boardStall) this.boardStall = stalls[0];
    const b = leaderboards.board(this.boardStall, this.boardScope);

    const scopeTabs = ['weekly', 'alltime'].map((s) =>
      `<button data-action="board-scope" data-scope="${s}" class="${s === this.boardScope ? 'on' : ''}">${s === 'weekly' ? 'This week' : 'All-time'}</button>`).join('');

    const stallTabs = stalls.map((id) =>
      `<button data-action="board-stall" data-stall="${id}" class="${id === this.boardStall ? 'on' : ''}">${esc(leaderboards.board(id, this.boardScope).stallName)}</button>`).join('');

    const rows = b.rows.map((r) => `<tr class="${r.isPlayer ? 'me' : ''}">
      <td style="width:44px">${r.rank}</td><td>${esc(r.name)}</td>
      <td class="r">${r.score}</td></tr>`).join('');

    return `<div class="tabs" style="padding:0 0 10px">${scopeTabs}</div>
      <div class="tabs" style="padding:0 0 14px">${stallTabs}</div>
      <table>${rows}</table>
      <div class="hint">You are rank ${b.playerRank} of ${b.rows.length} at ${esc(b.stallName)}
        (${b.scope === 'weekly' ? `week ${esc(b.weekKey)} — resets Monday` : 'all-time'}).
        Rival scores are simulated locally; there is no server.</div>`;
  }

  /** @private GDD 8.2 — the Star source. */
  _challenges() {
    const list = challenges.list();
    return list.map((c) => `<div class="row">
      <div class="t"><b>${esc(c.text)}</b><i>${c.progress} / ${c.target}${c.done ? ' · complete' : ''}</i>
        <div class="bar" style="margin:6px 0 0"><i style="width:${Math.round(c.pct * 100)}%"></i></div>
      </div>
      <div style="color:${c.done ? '#6fe7c4' : '#ffd166'};font-weight:bold">${c.done ? '✓' : ''} ${c.stars}⭐</div>
    </div>`).join('') +
      `<div class="hint">Three challenges rotate every Monday (week ${esc(challenges.weekKey())}).
        Stars buy shelf and tank upgrades in your personal space.</div>`;
  }

  /** @private GDD 8.3 + 12.4 — the demo store. */
  _store() {
    if (!store.isEnabled()) {
      return '<div class="hint">The store is switched off.</div>';
    }
    return `<div class="demo">${esc(STORE_CONFIG.demoLabel)}</div>` +
      store.packs().map((p) => `<div class="row">
        <div class="t"><b>${esc(p.name)} — ${p.tickets} tickets</b>
          <i>${p.priceLabel}${p.bonusLabel ? ` · ${p.bonusLabel} bonus` : ''} · ${p.perUnit} tickets per $</i></div>
        <button class="buy" data-action="buy-pack" data-pack="${p.id}">Get (demo)</button>
      </div>`).join('') +
      '<div class="hint">Nothing here charges you. Tickets are granted instantly.</div>';
  }

  // ── Interaction ─────────────────────────────────────────────────────────

  /** @private */
  _onClick(e) {
    const el = e.target.closest('[data-action]');
    if (!el) return;
    e.preventDefault();
    const { action } = el.dataset;

    switch (action) {
      case 'close': this.close(); return;
      case 'tab': this.screen = el.dataset.tab; break;
      case 'board-scope': this.boardScope = el.dataset.scope; break;
      case 'board-stall': this.boardStall = el.dataset.stall; break;
      case 'buy-piece': sets.purchase(el.dataset.piece); break;
      case 'equip': cosmetics.equip(el.dataset.slot, el.dataset.id); break;
      case 'unequip': cosmetics.unequip(el.dataset.slot); break;
      case 'claim-daily': dailyLogin.claim(); break;
      case 'buy-pack': this._buyPack(el.dataset.pack); return;
      default: break;
    }
    this.render();
  }

  /**
   * @private GDD 12.4 — the confirm step must name the demo explicitly, so no
   * one can mistake this for a real transaction.
   */
  _buyPack(packId) {
    const proceed = typeof window !== 'undefined' && typeof window.confirm === 'function'
      ? window.confirm(store.confirmText(packId))
      : true;
    if (proceed) store.buy(packId);
    this.render();
  }
}

/** The one overlay. */
export const overlay = new MetaOverlay();
export default overlay;
